import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, Star } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Jewellery', path: '/jewellery' },
  { name: 'Style', path: '/style' },
  { name: 'Collections', path: '/collections' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { cartCount, toggleCart } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-effect border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="logo-script text-3xl md:text-4xl text-[#D4AF37] hover:text-[#F2D06B] transition-colors"
          >
            LuxuryStyle
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`nav-link font-accent text-xs uppercase tracking-[0.2em] transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37]'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right side - Review, Cart & Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Google Review Button - Desktop */}
            <a
              href="https://share.google/jNF6BvocJeA38Cdry"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-google-review"
              className="hidden md:inline-flex items-center space-x-1.5 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] text-[#D4AF37] text-xs font-accent uppercase tracking-wider transition-all"
            >
              <Star className="w-3.5 h-3.5 fill-[#D4AF37]" strokeWidth={1.5} />
              <span>Review</span>
            </a>

            {/* Cart Button */}
            <button
              onClick={toggleCart}
              data-testid="cart-button"
              className="relative p-2 text-white hover:text-[#D4AF37] transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-black text-xs flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-white hover:text-[#D4AF37] transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" strokeWidth={1.5} />
              ) : (
                <Menu className="w-6 h-6" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            mobileMenuOpen ? 'max-h-[500px] pb-6' : 'max-h-0'
          }`}
        >
          <div className="flex flex-col space-y-4 pt-4 border-t border-white/10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className={`font-accent text-sm uppercase tracking-[0.2em] py-2 transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37]'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {/* Google Review Button - Mobile */}
            <a
              href="https://share.google/jNF6BvocJeA38Cdry"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-google-review"
              className="inline-flex items-center space-x-2 px-4 py-2.5 mt-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] font-accent text-sm uppercase tracking-[0.2em] transition-all"
            >
              <Star className="w-4 h-4 fill-[#D4AF37]" strokeWidth={1.5} />
              <span>Leave a Review</span>
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
